import { NAlert, NButton, NCard, NProgress, NSpace, NSpin, NTag, NText } from 'naive-ui';
import { computed, defineComponent, toRef } from 'vue';
import { useGuidedTourStore } from './guidedTourStore';
import { useBubblePosition } from './useBubblePosition';
import { useTargetRect } from './useTargetRect';
import { useTourActionListener } from './useTourActionListener';
import './guidedTour.scss';

/**
 * O balão do passo atual, ancorado no elemento real da tela.
 *
 * O recorte deixa o elemento clicável: nos passos de ação, a pessoa faz a coisa de
 * verdade — digita a busca, clica no contrato — e o tour avança sozinho. É a diferença
 * entre assistir a um vídeo e operar a plataforma.
 */
export const GuidedTourOverlay = defineComponent({
	name: 'GuidedTourOverlay',

	setup() {
		const tour = useGuidedTourStore();

		const targetId = computed(() => tour.currentStep?.target);
		const { rect } = useTargetRect(toRef(targetId, 'value') as any);

		// Quem avança os passos de ação é a interação real da pessoa na tela.
		useTourActionListener();

		const placement = computed(() => tour.currentStep?.placement);
		const { setBubble, style: bubbleStyle, measured } = useBubblePosition(rect, placement);

		return { tour, rect, bubbleStyle, setBubble, measured };
	},

	render() {
		const step = this.tour.currentStep;

		if (!this.tour.isRunning || !step) return null;

		// A tela ainda não montou o que este passo precisa. Mostrar o passo agora faria
		// ele apontar para o vazio, e a pessoa ficaria clicando tentando adivinhar.
		if (this.tour.waiting) {
			return (
				<>
					<div class="gt-overlay" />
					<div class="gt-bubble gt-bubble--centered">
						<NCard size="small" style="box-shadow: 0 10px 34px rgba(0,0,0,.3);">
							<NSpace align="center" size={12}>
								<NSpin size="small" />
								<NText depth={2}>Preparando o próximo passo…</NText>
							</NSpace>
						</NCard>
					</div>
				</>
			);
		}

		const hasTarget = !!this.rect;
		const isWarning = !!step.warning;
		const isAction = step.advance === 'action';

		const spotlightClass = [
			'gt-spotlight',
			isWarning ? 'gt-spotlight--warning' : '',
			isAction ? 'gt-spotlight--action' : '',
		]
			.filter(Boolean)
			.join(' ');

		return (
			<>
				{/* Sem alvo visível, escurece a tela inteira e centraliza o balão. */}
				{!hasTarget && <div class="gt-overlay" />}

				{hasTarget && (
					<div
						class={spotlightClass}
						style={`top: ${this.rect!.top}px; left: ${this.rect!.left}px; width: ${this.rect!.width}px; height: ${this.rect!.height}px;`}
					/>
				)}

				<div
					ref={(el) => this.setBubble((el as HTMLElement) ?? null)}
					class={`gt-bubble ${hasTarget ? '' : 'gt-bubble--centered'}`}
					style={
						hasTarget
							? `${this.bubbleStyle ?? ''}${this.measured ? '' : ' visibility: hidden;'}`
							: undefined
					}
				>
					<NCard size="small" style="box-shadow: 0 10px 34px rgba(0,0,0,.3);">
						{{
							header: () => (
								<div>
									<NText class="gt-bubble__step">
										Passo {this.tour.stepIndex + 1} de {this.tour.totalSteps}
									</NText>
									<br />
									<NText strong style="font-size: 15px;">
										{step.title}
									</NText>
								</div>
							),
							'header-extra': () => (
								<NButton quaternary circle size="small" onClick={() => this.tour.leave()}>
									✕
								</NButton>
							),
							default: () => (
								<div>
									<NProgress
										type="line"
										showIndicator={false}
										status={isWarning ? 'error' : isAction ? 'warning' : 'success'}
										percentage={this.tour.progressPercent}
										style="margin-bottom: 12px;"
									/>

									<NText depth={1}>{step.body}</NText>

									{step.hint && (
										<NAlert type="default" style="margin-top: 10px;">
											<NText depth={2} style="font-size: 12px;">
												💡 {step.hint}
											</NText>
										</NAlert>
									)}

									{step.warning && (
										<NAlert type="error" title="Atenção" style="margin-top: 12px;">
											{step.warning}
										</NAlert>
									)}

									{/* Aparece quando a pessoa clicou em "Já fiz isso" sem ter feito. */}
									{this.tour.blockedMessage && (
										<NAlert type="warning" style="margin-top: 12px;">
											{this.tour.blockedMessage}
										</NAlert>
									)}

									{isAction && (
										<NTag
											size="small"
											type="warning"
											bordered={false}
											style="margin-top: 10px;"
										>
											Sua vez — eu sigo sozinho assim que você fizer
										</NTag>
									)}
								</div>
							),
							action: () => (
								<NSpace justify="space-between" align="center">
									<NButton
										quaternary
										size="small"
										disabled={this.tour.stepIndex === 0}
										onClick={() => this.tour.previous()}
									>
										Voltar
									</NButton>

									<NSpace size={8}>
										{/*
										 * Sair sempre existe, inclusive no tutorial, e leva de volta
										 * às boas-vindas. Prender alguém numa tela e obrigá-la a
										 * fechar o navegador para escapar custa mais confiança do
										 * que o tutorial ganha em conclusões forçadas.
										 */}
										<NButton quaternary size="small" onClick={() => this.tour.leave()}>
											Sair
										</NButton>

										{/*
										 * No passo de ação quem avança é a tela. Este botão é a saída
										 * para quem clicou por outro caminho e o tour não percebeu —
										 * mas ele confere o estado antes de andar, senão viraria um
										 * jeito de pular passo e se perder dois passos à frente.
										 */}
										<NButton
											type={isAction ? 'default' : isWarning ? 'error' : 'primary'}
											quaternary={isAction}
											size="small"
											onClick={() => this.tour.confirmAction()}
										>
											{isAction
												? 'Já fiz, seguir'
												: this.tour.isLastStep
													? 'Entendi, pode fechar'
													: 'Próximo'}
										</NButton>
									</NSpace>
								</NSpace>
							),
						}}
					</NCard>
				</div>
			</>
		);
	},
});

export default GuidedTourOverlay;
